import { create, StateCreator } from "zustand";
import EditorCell from "../EditorCell.tsx";
import EditorPrototype from "../EditorPrototype.tsx";
import { Vector3 } from "three";
import { subscribeWithSelector } from "zustand/middleware";

export interface EditorState {
  mapSize: Vector3;
  map: EditorCell[][][]; // TODO : Change this to a proper type
  setMap: (map: EditorCell[][][]) => void;
  selectedPrototype: EditorPrototype | null;
  setSelectedPrototype: (prototype: EditorPrototype | null) => void;
}

function hasAdjacentPrototype(
  map: EditorCell[][][],
  x: number,
  y: number,
  z: number,
): boolean {
  const directions = [
    { dx: -1, dy: 0, dz: 0 },
    { dx: 1, dy: 0, dz: 0 },
    { dx: 0, dy: -1, dz: 0 },
    { dx: 0, dy: 1, dz: 0 },
    { dx: 0, dy: 0, dz: -1 },
    { dx: 0, dy: 0, dz: 1 },
  ];

  for (const { dx, dy, dz } of directions) {
    const nx = x + dx;
    const ny = y + dy;
    const nz = z + dz;

    if (
      nx >= 0 &&
      nx < map.length &&
      ny >= 0 &&
      ny < map[0].length &&
      nz >= 0 &&
      nz < map[0][0].length
    ) {
      if (map[nx][ny][nz].prototype) {
        return true;
      }
    }
  }

  return false;
}

export function setEditableCells(map: EditorCell[][][]): EditorCell[][][] {
  for (let x = 0; x < map.length; x++) {
    for (let y = 0; y < map[x].length; y++) {
      for (let z = 0; z < map[y].length; z++) {
        if (y === 0 || hasAdjacentPrototype(map, x, y, z)) {
          map[x][y][z].editable = true;
        } else {
          map[x][y][z].editable = false;
        }
      }
    }
  }
  return map;
}

export function initEmptyMap(mapSize: Vector3): EditorCell[][][] {
  const map: EditorCell[][][] = [];

  for (let x = 0; x < mapSize.x; x++) {
    map[x] = [];
    for (let y = 0; y < mapSize.y; y++) {
      map[x][y] = [];
      for (let z = 0; z < mapSize.z; z++) {
        if (y === 0) {
          map[x][y][z] = new EditorCell(new Vector3(x, y, z));
          continue;
        }
        map[x][y][z] = new EditorCell(new Vector3(x, y, z));
      }
    }
  }
  return setEditableCells(map);
}

export function setSelectedPrototypeToMapAt(
  map: EditorCell[][][],
  position: Vector3,
  selectedPrototype: EditorPrototype | null,
): EditorCell[][][] {
  map[position.x][position.y][position.z].prototype = selectedPrototype;
  return setEditableCells(map);
}

const mapsSize = new Vector3(10, 10, 10);

const editorStateCreator: StateCreator<EditorState> = (set) => ({
  mapSize: mapsSize,
  map: initEmptyMap(mapsSize),
  selectedPrototype: null,
  setSelectedPrototype: (prototype: EditorPrototype | null) => {
    set(() => ({ selectedPrototype: prototype }));
  },
  setMap: (map: EditorCell[][][]) => {
    set(() => ({ map }));
  },
});

export const useEditorStore = create<EditorState>()(
  subscribeWithSelector(editorStateCreator),
);
